/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener("DOMContentLoaded", () => {
  const product_star = document.querySelector("[scroll-to-review]");
  product_star?.addEventListener(
    "click",
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const review_tab = getReviewTarget();
      if (!review_tab) return;

      if (review_tab.getAttribute("aria-expanded") === "false") {
        review_tab.click();
      }

      const product_sticky_form = document.querySelector(".product-sticky-form");
      const sticky_header = document.querySelector("store-header");
      const total_height =
        (product_sticky_form?.offsetHeight || 0) +
        (sticky_header?.offsetHeight || 0) +
        20;

      review_tab.style.scrollMarginTop = `${total_height}px`;
      review_tab.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    true
  );
});

const getReviewTarget = () => {
  const reviews_widget = [
    ...document.querySelectorAll("[data-reviewsio-widget='polaris']"),
  ].find(isElementVisible);
  if (reviews_widget) return reviews_widget;

  const review_tab = [...document.querySelectorAll("[review-tab]")].find(
    isElementVisible
  );
  if (review_tab) return review_tab;

  const review_button = [...document.querySelectorAll("[review-button]")].find(
    isElementVisible
  );
  if (review_button) return review_button;

  return (
    document
      .querySelector("#ReviewsWidget")
      ?.closest("product-tab")
      ?.querySelector("[is='toggle-button']") || null
  );
};

const isElementVisible = (el) => {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none") return false;
  if (style.visibility !== "visible") return false;
  if (style.opacity < 0.1) return false;
  let element = el;
  while (element) {
    if (element === document) return true;
    if (element.nodeName === "BODY") return true;
    const element_style = window.getComputedStyle(element);
    if (element_style.display === "none") return false;
    if (element_style.visibility !== "visible") return false;
    if (element_style.opacity < 0.1) return false;
    element = element.parentNode;
  }
  return true;
};

class ProductTab extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.button = this.querySelector("button");
    this.initButtons(this.button);
  }

  initButtons(button) {
    button.addEventListener("click", () => {
      const allTabs = document.querySelectorAll("product-tab");
      allTabs.forEach((tab) => {
        if (tab !== this) {
          const collapsibleContent = tab.querySelector('collapsible-content');
          collapsibleContent.open = false;
        }
      });
    });
  }
}
if (!window.customElements.get("product-tab")) {
  window.customElements.define("product-tab", ProductTab);
}
// DEV: TO HIDE GEOLOCATION POPUP
// // wait for element with ID #openGeo and remove it
// // use Mutation observer to check if element added to body
// const observer = new MutationObserver((mutations) => {
//   mutations.forEach((mutation) => {
//     mutation.addedNodes.forEach((node) => {
//       if (node.id === "openGeo") {
//         node.remove();
//         observer.disconnect();
//       }
//     });
//   });
// });
// observer.observe(document.body, { childList: true });

document.addEventListener('DOMContentLoaded', function() {
  // USED TO TRACK REMOVED ITEMS FROM CART for XY GIFT LOGIC
  fetch(`${window.themeVariables.routes.cartUrl}.js`)
    .then( response => response.json())
    .then(cart =>  window.currCartItems = cart.items);
});
const calculateRemovedItems = (oldCartItems, currCartItems) => {
  const cartItemsIds = currCartItems.map(item => item.variant_id);
  const oldCartItemsIds = oldCartItems.map(item => item.variant_id);
  const removedItemIds = oldCartItemsIds.filter(id => !cartItemsIds.includes(id));
  window.currCartItems = currCartItems;
  return removedItemIds;
}
const calcUpdatesObj = (cartItemIds,removedItemIds) => {
  const updates = {}
  window.xyGiftLogic.forEach(rule => {
    const yProductRemoved = removedItemIds.includes(parseInt(rule.yProduct.variantID));
    const xProductInCart = cartItemIds.includes(parseInt(rule.xProduct.variantID));
    const xProductRemoved = removedItemIds.includes(parseInt(rule.xProduct.variantID));
    const yProductInCart = cartItemIds.includes(parseInt(rule.yProduct.variantID));
    // when x Product is added and y Product is not in cart. ( don't add back if y Product is removed)
    if(xProductInCart && !yProductInCart && !yProductRemoved){
      updates[rule.yProduct.variantID] = 1;
    }
    // when x Product is removed and y Product is in cart.
    if(xProductRemoved && yProductInCart){
      updates[rule.yProduct.variantID] = 0;
    }
  });
  return updates;
}
const hasPostCartRules = window.samplePickerConfig?.samples?.length || window.xyGiftLogic?.length 
  
if ( hasPostCartRules && !window.cartBarIsOn){
  document.addEventListener('cart:updated', function(event) {
    const cart = event.detail.cart;
    const cartItemIds = event.detail.cart.items.map(item => item.variant_id);
    const removedItemIds = calculateRemovedItems(window.currCartItems, event.detail.cart.items);
    const updates = calcUpdatesObj(cartItemIds,removedItemIds);

    if (window.samplePickerConfig?.samples?.length) {
      const sampleUpdates = getSampleCleanupUpdates(cart?.items);
      Object.assign(updates, sampleUpdates);
    }

    if (Object.keys(updates).length > 0){
      fetch(`${window.themeVariables.routes.cartUpdateUrl}.js`, {
        body: JSON.stringify({
          updates: updates
        }),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }).then( async (response) => {
        const cartContent = await response.json();
        window.currCartItems = cartContent.items;
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartContent,
              replacementDelay: 1000
            },
          })
        )
      });
    }
  })
}
